/**
 * Smart Toast Utility
 * 
 * Wraps sonner's toast to provide:
 * - Deduplication (prevents same message from showing multiple times)
 * - Smart ID generation for common messages
 * - Rate limiting for frequent toasts
 * - Grouped notifications for batch operations
 * - Cascading error suppression (prevents multiple error toasts when one root cause triggers many)
 * - Category-based grouping (shows one message per error category, not per API call)
 */

import { toast as sonnerToast } from 'sonner';

// =============================================================================
// CASCADING ERROR SUPPRESSION
// When multiple API calls fail simultaneously (e.g., on page load), we want to
// show ONE clear error message, not a cascade of confusing toasts.
// =============================================================================

// Track active error categories to suppress duplicates
const activeErrorCategories = new Map(); // category -> { timestamp, primaryMessage }
const ERROR_CATEGORY_WINDOW_MS = 5000; // Suppress related errors for 5 seconds

// Error categories - group related errors together
const ERROR_CATEGORIES = {
  // Network/connectivity issues
  NETWORK: ['network', 'connection', 'offline', 'timeout', 'fetch failed', 'net::err'],
  // Authentication issues  
  AUTH: ['unauthorized', '401', 'session expired', 'login', 'token'],
  // Permission/access issues
  PERMISSION: ['forbidden', '403', 'permission', 'access denied', 'not allowed'],
  // Subscription issues
  SUBSCRIPTION: ['subscription', 'trial', 'subscribe', 'plan', 'expired'],
  // Data loading issues
  LOAD_DATA: ['unable to load', 'failed to load', 'failed to fetch', 'could not load', 'error loading'],
  // Server errors
  SERVER: ['500', 'server error', 'internal error', 'something went wrong'],
};

/**
 * Detect the error category from a message
 */
function detectErrorCategory(message) {
  if (typeof message !== 'string') return null;
  const lowerMessage = message.toLowerCase();
  
  for (const [category, keywords] of Object.entries(ERROR_CATEGORIES)) {
    if (keywords.some(keyword => lowerMessage.includes(keyword))) {
      return category;
    }
  }
  return null;
}

/**
 * Check if an error category is currently active (another error of same type shown recently)
 */
function isErrorCategorySuppressed(category) {
  if (!category) return false;
  
  const active = activeErrorCategories.get(category);
  if (!active) return false;
  
  // Check if still within suppression window
  if (Date.now() - active.timestamp < ERROR_CATEGORY_WINDOW_MS) {
    return true;
  }
  
  // Expired, remove it
  activeErrorCategories.delete(category);
  return false;
}

/**
 * Mark an error category as active (first error of this type shown)
 */
function setErrorCategoryActive(category, message) {
  if (!category) return;
  activeErrorCategories.set(category, {
    timestamp: Date.now(),
    primaryMessage: message,
  });
}

/**
 * Clear error category suppression (e.g., when user navigates away)
 */
export function clearErrorSuppression() {
  activeErrorCategories.clear();
}

// Track recently shown toasts for deduplication
const recentToasts = new Map(); // message hash -> timestamp
const DEDUP_WINDOW_MS = 3000; // Don't show same toast within 3 seconds

// Rate limiting
const toastCounts = new Map(); // category -> count in window
const RATE_LIMIT_WINDOW_MS = 5000;
const MAX_TOASTS_PER_CATEGORY = 2; // Reduced from 3 to be more aggressive

/**
 * Generate a hash for a toast message for deduplication
 */
function hashMessage(message) {
  if (typeof message === 'string') {
    return message;
  }
  // For React elements, use a simplified string representation
  if (message?.props) {
    return JSON.stringify(message.props).slice(0, 100);
  }
  return String(message);
}

/**
 * Check if we should show this toast (deduplication)
 */
function shouldShowToast(message, options = {}) {
  // Skip deduplication if explicitly disabled
  if (options.allowDuplicate) {
    return true;
  }
  
  const hash = hashMessage(message);
  const now = Date.now();
  
  // Check if same message was shown recently
  const lastShown = recentToasts.get(hash);
  if (lastShown && (now - lastShown) < DEDUP_WINDOW_MS) {
    return false;
  }
  
  // Mark as shown
  recentToasts.set(hash, now);
  
  // Clean up old entries periodically
  if (recentToasts.size > 50) {
    for (const [key, time] of recentToasts) {
      if (now - time > DEDUP_WINDOW_MS) {
        recentToasts.delete(key);
      }
    }
  }
  
  return true;
}

/**
 * Check rate limiting for a category
 */
function checkRateLimit(category) {
  if (!category) return true;
  
  const now = Date.now();
  const key = `${category}-${Math.floor(now / RATE_LIMIT_WINDOW_MS)}`;
  
  const count = toastCounts.get(key) || 0;
  if (count >= MAX_TOASTS_PER_CATEGORY) {
    return false;
  }
  
  toastCounts.set(key, count + 1);
  
  // Clean up old keys
  if (toastCounts.size > 20) {
    const currentWindow = Math.floor(now / RATE_LIMIT_WINDOW_MS);
    for (const k of toastCounts.keys()) {
      const windowNum = parseInt(k.split('-').pop(), 10);
      if (windowNum < currentWindow - 1) {
        toastCounts.delete(k);
      }
    }
  }
  
  return true;
}

/**
 * Generate a stable ID for common toast types
 */
function generateToastId(type, message, options = {}) {
  if (options.id) {
    return options.id;
  }
  
  // For certain message patterns, use stable IDs
  if (typeof message === 'string') {
    if (message.includes('upload') || message.includes('Upload')) {
      return `upload-${hashMessage(message).slice(0, 20)}`;
    }
    if (message.includes('dashboard') || message.includes('Dashboard')) {
      return `dashboard-${type}`;
    }
    if (message.includes('network') || message.includes('Network')) {
      return `network-error`;
    }
    // Subscription-related toasts - prevent duplicates
    if (message.includes('subscription') || message.includes('Subscription') || message.includes('update your subscription')) {
      return 'subscription-error';
    }
  }
  
  return undefined; // Let sonner generate ID
}

/**
 * Wrapped toast functions with deduplication
 */
const smartToast = {
  success: (message, options = {}) => {
    if (!shouldShowToast(message, options)) return null;
    if (!checkRateLimit(options.category)) return null;
    
    const id = generateToastId('success', message, options);
    return sonnerToast.success(message, { ...options, id });
  },
  
  error: (message, options = {}) => {
    // CASCADING ERROR SUPPRESSION: Prevent multiple related errors from showing
    // If an error of the same category was recently shown, suppress this one
    const errorCategory = detectErrorCategory(message);
    
    if (errorCategory && !options.allowDuplicate) {
      if (isErrorCategorySuppressed(errorCategory)) {
        // Suppressed cascading error toast
        return null;
      }
      // First error of this category - mark it active
      setErrorCategoryActive(errorCategory, message);
    }
    
    if (!shouldShowToast(message, options)) return null;
    if (!checkRateLimit(options.category)) return null;
    
    const id = generateToastId('error', message, options);
    return sonnerToast.error(message, { ...options, id });
  },
  
  warning: (message, options = {}) => {
    // Apply same cascading suppression to warnings
    const errorCategory = detectErrorCategory(message);
    
    if (errorCategory && !options.allowDuplicate) {
      if (isErrorCategorySuppressed(errorCategory)) {
        // Suppressed cascading warning toast
        return null;
      }
      setErrorCategoryActive(errorCategory, message);
    }
    
    if (!shouldShowToast(message, options)) return null;
    if (!checkRateLimit(options.category)) return null;
    
    const id = generateToastId('warning', message, options);
    return sonnerToast.warning(message, { ...options, id });
  },
  
  info: (message, options = {}) => {
    // Only suppress info toasts that are error-related
    const isErrorRelated = typeof message === 'string' && 
      (message.toLowerCase().includes('failed') || 
       message.toLowerCase().includes('error') ||
       message.toLowerCase().includes('unable to'));
    
    if (isErrorRelated) {
      const errorCategory = detectErrorCategory(message);
      if (errorCategory && !options.allowDuplicate && isErrorCategorySuppressed(errorCategory)) {
        // Suppressed cascading info toast
        return null;
      }
    }
    
    if (!shouldShowToast(message, options)) return null;
    if (!checkRateLimit(options.category)) return null;
    
    const id = generateToastId('info', message, options);
    return sonnerToast.info(message, { ...options, id });
  },
  
  loading: (message, options = {}) => {
    // Loading toasts should always show (they're progress indicators)
    const id = generateToastId('loading', message, options);
    return sonnerToast.loading(message, { ...options, id });
  },
  
  promise: (promise, options = {}) => {
    return sonnerToast.promise(promise, options);
  },
  
  dismiss: (id) => {
    return sonnerToast.dismiss(id);
  },
  
  // Bypass deduplication for important messages
  important: {
    success: (message, options = {}) => {
      return sonnerToast.success(message, { ...options, allowDuplicate: true });
    },
    error: (message, options = {}) => {
      return sonnerToast.error(message, { ...options, allowDuplicate: true });
    },
    warning: (message, options = {}) => {
      return sonnerToast.warning(message, { ...options, allowDuplicate: true });
    },
    info: (message, options = {}) => {
      return sonnerToast.info(message, { ...options, allowDuplicate: true });
    },
  },
  
  // For upload-specific toasts with proper deduplication
  upload: {
    progress: (message, options = {}) => {
      const id = options.uploadId || 'upload-progress';
      return sonnerToast.loading(message, { ...options, id, duration: Infinity });
    },
    success: (message, options = {}) => {
      const id = options.uploadId || generateToastId('success', message, options);
      // Dismiss any loading toast with same ID
      sonnerToast.dismiss(id);
      return sonnerToast.success(message, { ...options, id: `${id}-done`, duration: 6000 });
    },
    error: (message, options = {}) => {
      const id = options.uploadId || generateToastId('error', message, options);
      sonnerToast.dismiss(id);
      return sonnerToast.error(message, { ...options, id: `${id}-error`, duration: 8000 });
    },
  },
  
  // Clear deduplication cache (useful for testing)
  clearCache: () => {
    recentToasts.clear();
    toastCounts.clear();
  },
};

// Also export the original toast for cases where deduplication isn't wanted
export { sonnerToast as rawToast };

export default smartToast;
